import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface UploadConfig {
  module: string; // 'vendor', 'client', 'item', etc.
  formTemplateId?: string | number;
  additionalParams?: Record<string, string | number>;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Generic bulk upload method
   * @param file - The file to upload
   * @param config - Upload configuration (module, formTemplateId, etc.)
   * @returns Observable with upload response
   */
  uploadFile(file: File, config: UploadConfig): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    // Build query parameters
    let params = new HttpParams();
    
    if (config.formTemplateId) {
      params = params.set('form_template_id', config.formTemplateId.toString());
    }

    // Add any additional parameters
    if (config.additionalParams) {
      Object.keys(config.additionalParams).forEach(key => {
        params = params.set(key, config.additionalParams![key].toString());
      });
    }

    // Construct the endpoint based on module
    const endpoint = this.getUploadEndpoint(config.module);

    return this.http.post<any>(`${this.API_BASE_URL}${endpoint}`, formData, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get the upload endpoint for a given module
   * @param module - Module name (vendor, client, item, inventory, etc.)
   * @returns API endpoint path
   */
  private getUploadEndpoint(module: string): string {
    const moduleEndpoints: Record<string, string> = {
      // Base modules
      'vendor': '/api/v1/upload/vendor',
      'client': '/api/v1/upload/client',
      'item': '/api/v1/upload/item',
      'inventory': '/api/v1/upload/inventory',
      
      // Vendor sub-modules
      'vendor-po': '/api/v1/upload/vendor-po',
      'vendor-invoice': '/api/v1/upload/vendor_invoice',
      'vendor-payment': '/api/v1/upload/vendor-payment',
      'vendor-return': '/api/v1/upload/vendor-return',
      'vendor-debit-note': '/api/v1/upload/vendor-debit-note',
      'grn': '/api/v1/upload/grn',
      
      // Client sub-modules
      'client-order': '/api/v1/upload/client-order',
      'client-invoice': '/api/v1/upload/client-invoice',
      'client-payment': '/api/v1/upload/client-payment',
      'client-return': '/api/v1/upload/client-return',
      'client-refund': '/api/v1/upload/client-refund',
      'credit-note': '/api/v1/upload/credit-note',
    };

    // Check for exact match first
    if (moduleEndpoints[module]) {
      return moduleEndpoints[module];
    }

    // For sub-modules, try to extract base module
    if (module.includes('vendor')) {
      return '/api/v1/upload/vendor';
    }
    if (module.includes('client')) {
      return '/api/v1/upload/client';
    }
    if (module.includes('item')) {
      return '/api/v1/upload/item';
    }
    if (module.includes('inventory')) {
      return '/api/v1/upload/inventory';
    }

    // Default fallback
    return `/api/v1/upload/${module}`;
  }
}

